/** App-side record shapes that live in IndexedDB next to the core model types. */
import type {
  AvailabilityTable,
  AvailabilityVote,
  BasketEntry,
  Favourite,
  Food,
  Recipe,
  RegionCode,
  SyncKind,
  Trip,
} from '@resupport/core'
import type { Settings } from './settings'

/**
 * This device's own availability rating for a category (or one food) in one region. Same
 * fields as the vote that shares it, plus an id and a soft delete so a cleared rating syncs.
 */
export interface UserRating extends AvailabilityVote {
  /** `ratingKey(category, food, region)`. */
  id: string
  deleted?: boolean
}

/** "noodles||ES", "noodles|indomie-mi-goreng|ES". */
export function ratingKey(category: string, food: string | undefined, region: RegionCode): string {
  return `${category}|${food ?? ''}|${region}`
}

/** The plan-free basket: shopping with no trip behind it. Counts up, or down from a goal. */
export interface ScratchBasket {
  entries: BasketEntry[]
  /**
   * Optional goal for the countdown. When `days` or `kcal_per_day` is set (through
   * `Store.updateScratch`), always `round(days × kcal_per_day)`.
   */
  kcal_goal?: number
  /** What the goal was built from: days of food… */
  days?: number
  /** …at this many kcal a day. */
  kcal_per_day?: number
  region?: RegionCode
  updated_at?: string
}

/** kcal a day for a scratch goal when nobody has said. */
export const DEFAULT_SCRATCH_KCAL_PER_DAY = 3500

/**
 * The scratch goal as days × kcal a day. Older baskets stored only `kcal_goal`: their days
 * are derived from it (to the half day) at the default or stored kcal a day.
 */
export function scratchGoalOf(basket: ScratchBasket): { days: number; kcal_per_day: number } {
  const kpd =
    basket.kcal_per_day !== undefined && basket.kcal_per_day > 0
      ? basket.kcal_per_day
      : DEFAULT_SCRATCH_KCAL_PER_DAY
  if (basket.days !== undefined) return { days: basket.days, kcal_per_day: kpd }
  const goal = basket.kcal_goal ?? 0
  return { days: goal > 0 ? Math.round((goal / kpd) * 2) / 2 : 0, kcal_per_day: kpd }
}

/** Sync bookkeeping, persisted in meta. Never contains user data, only ids and cursors. */
export interface SyncMeta {
  token?: string
  account_id?: string
  /** The server_time of the last successful pull; the next `since`. */
  since?: string
  /** Opaque continuation for a pull that did not fit one response. */
  cursor?: string
  last_synced_at?: string
  last_error?: string
  /**
   * Records changed here and not yet accepted by the server: `${kind}:${id}` → the
   * updated_at that was current when it changed. Tracked even with sync off (harmless, and
   * nothing reads it until consent) so that turning sync on later pushes everything.
   */
  dirty: Record<string, string>
  /** The last time shared votes went up; ratings updated after it go next time. */
  votes_pushed_at?: string
}

export const EMPTY_SYNC: SyncMeta = { dirty: {} }

export const EMPTY_BASKET: ScratchBasket = { entries: [] }

export type DirtyKey = `${SyncKind}:${string}`

/** What Settings → Export writes and Import reads. */
export interface ExportFile {
  format: 'resupport-export'
  version: 1
  exported_at: string
  trips: Trip[]
  foods: Food[]
  recipes: Recipe[]
  ratings: UserRating[]
  /** Starred foods and recipes. Absent in files from before favourites. */
  favourites?: Favourite[]
  settings?: Settings
  basket?: ScratchBasket
}

export interface ImportSummary {
  trips: number
  foods: number
  recipes: number
  ratings: number
  /** Present when the file carried favourites. */
  favourites?: number
  /** Records that were older than what is already here, or did not parse. */
  skipped: number
}

export type { AvailabilityTable }
